import React, { useState, useEffect } from 'react';

const Other = ({ product, updateProduct, handleFileChange }) => {
  const [description, setDescription] = useState(product.fields.description || '');
  const [referenceImage, setReferenceImage] = useState(product.fields.referenceImage || product.fields.referenceImageUrl || '');

  // Update reference image when product fields change
  useEffect(() => {
    if (product.fields.referenceImageUrl) {
      setReferenceImage(product.fields.referenceImageUrl);
    }
  }, [product.fields.referenceImageUrl]);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
    updateProduct('description', value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a preview URL for the image
      const imageUrl = URL.createObjectURL(file);
      setReferenceImage(imageUrl);
      // Don't store the local blob URL in product fields
      
      // Use the handleFileChange function from parent to upload the file
      if (handleFileChange) {
        const syntheticEvent = {
          target: {
            files: [file],
            name: 'referenceImage'
          }
        };
        handleFileChange(syntheticEvent);
      }
    }
  };

  const removeImage = () => {
    setReferenceImage('');
    // Don't store empty strings, just clear the local state
    updateProduct('referenceImageUrl', ''); // Clear the uploaded URL
  };

  return (
    <div className="product-form">
      {/* Reference Image Section */}
      <div className="form-group">
        <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">
          Reference Image:
        </label>
        <p className="text-sm text-gray-600 mb-2">
          Please upload a clear reference image of the product you need quoted.
        </p>
        
        {!referenceImage ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              id="reference-image-upload"
            />
            <label
              htmlFor="reference-image-upload"
              className="cursor-pointer flex flex-col items-center"
            >
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="mt-2 block text-sm font-medium text-gray-900">
                Click to upload reference image
              </span>
              <span className="mt-1 block text-sm text-gray-500">
                PNG, JPG, GIF up to 10MB
              </span>
            </label>
          </div>
        ) : (
          <div className="relative">
            <img
              src={referenceImage}
              alt="Product reference"
              className="w-full h-64 object-contain border border-gray-300 rounded-lg"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Product Description Section */}
      <div className="form-group">
        <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">
          Product Description:
        </label>
        <p className="text-sm text-gray-600 mb-2">
          Please provide a detailed description of the product you need quoted, including:
        </p>
        <ul className="text-sm text-gray-600 mb-3 list-disc list-inside">
          <li>Product type and purpose</li>
          <li>Dimensions and specifications</li>
          <li>Material requirements</li>
          <li>Quantity needed</li>
          <li>Any special requirements or features</li>
        </ul>
        <textarea
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
          rows="6"
          value={description}
          onChange={handleDescriptionChange}
          placeholder="Enter detailed product description here..."
        />
        <div className="mt-1 text-sm text-gray-500">
          Character count: {description.length}/1000
        </div>
      </div>

      {/* Additional Specifications */}
      <div className="form-group">
        <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">
          Additional Specifications (Optional):
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Quantity
            </label>
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              value={product.fields.estimatedQuantity || ''}
              onChange={(e) => updateProduct('estimatedQuantity', e.target.value)}
              placeholder="e.g., 1000 units"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Timeline
            </label>
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              value={product.fields.targetTimeline || ''}
              onChange={(e) => updateProduct('targetTimeline', e.target.value)}
              placeholder="e.g., 4-6 weeks"
            />
          </div>
        </div>
      </div>

      {/* Special Requirements */}
      <div className="form-group">
        <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">
          Special Requirements or Notes:
        </label>
        <textarea
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
          rows="3"
          value={product.fields.specialRequirements || ''}
          onChange={(e) => updateProduct('specialRequirements', e.target.value)}
          placeholder="Any additional requirements, constraints, or special notes..."
        />
      </div>
    </div>
  );
};

export default Other;
